import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./css/common.css";
import "./css/dashboard-premium.css";
import { Box, Typography } from "@mui/material";
import { BarChart, LineChart, PieChart, ScatterChart } from "@mui/x-charts";
import { post } from "../services/services";
import { ApiURL } from "../services/apiConstants";
import VoyageStatus from "../components/VoyageStatus";
import DateEngineChartSelector from "../components/DateEngineChartSelector";
import ShipDetailsCard from "../components/ShipDetailsCard";
import { SuccessToast } from "../widgets/AppToast";
import GenerateReportToolstaff from "../components/GenerateReportToolstaff";
import { GENERATEREPORT } from "../utils/downloadPdf";
import useVoyageList from "../hooks/useVoyageList";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { Select, Row, Col, Card, Typography as AntdTypography } from "antd";
import {
  DashboardOutlined,
  LineChartOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import PendingTaskTab from "../components/PendingTaskTab";
import useUserData from "../hooks/useUserData";

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

function StaffDashboard() {
  const user = useUserData();
  const designation = user?.data?.designation || "";
  const { voyageList } = useVoyageList();
  const [engineType, setEngineType] = useState([]);
  const [selectedChart, setSelectedChart] = useState("line");
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [series, setSeries] = useState([]);
  const [xAxis, setXAxis] = useState("");
  const [ship, setShip] = useState("");
  const [voyage, setVoyage] = useState("");
  const [selectedReport, setSelectedReport] = useState("ORB Part 1");
  const [startDates, setStartDates] = useState("");
  const [endDates, setEndDates] = useState("");
  const [reportDates, setReportDates] = useState(null);
  const [timeFormat, setTimeFormat] = useState("daily");
  const [engineOptions, setEngineOptions] = useState([]);
  const [parameterOptions, setParameterOptions] = useState({});
  const [voyageDetails, setVoyageDetails] = useState({});
  const [selectedLogbook, setSelectedLogbook] = useState(["engine_logbook"]);

  // console.log(' engineOptions ***************',engineOptions)

  // console.log("logbook", selectedLogbook);
  const GET_TAB_DATA_LIST = async (activeTab) => {
    try {
      const formData = new FormData();
      formData.append("type", activeTab);
      switch (activeTab) {
        case "engine-log":
        case "deck-log":
        case "oil-record-part-i":
        case "oil-record-part-ii":
        case "cargo-record":
        case "garbage-record-part-i":
        case "garbage-record-part-ii":
        case "ballast-record":
        case "sulphur-record-part-i":
        case "sulphur-record-part-ii":
        case "sulphur-record-part-iii":
          return await post(ApiURL.pending_entry_list_st, formData);
        default:
          return [];
      }
    } catch (error) {
      console.error("GET_TAB_DATA_LIST error:", error);
      return [];
    }
  };

  // Fetch dashboard details
  const fetchDashboardDetails = async () => {
    if (!navigator.onLine) return;
    try {
      const res = await post(ApiURL.staff_dashboard, new FormData());
      if (res?.status === true) {
        localStorage.setItem(
          "staff_dashboard",
          JSON.stringify(res?.data || null),
        );
      }
    } catch (error) {
      console.error("Error fetching Staff Dashboard data:", error);
    }
  };

  // Fetch graph data
  const fetchGraphData = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      const formData = new FormData();
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("log_book", JSON.stringify(selectedLogbook));
      formData.append("machines", JSON.stringify(selectedParameters));
      const res = await post(ApiURL.staff_dashboard_graph, formData);
      if (res?.status === true) {
        SuccessToast("Success Message !", res?.message);
        setSeries(res.data.series);
        setXAxis(res.data.xAxis);
      }
    } catch (error) {
      console.error("API call error:", error);
      localStorage.setItem("staff_dashboard", JSON.stringify(null));
    }
  }, [startDate, endDate, selectedParameters, selectedLogbook]);

  // Transform data by time format
  const transformDataByFormat = (rawXAxis, rawSeries, format) => {
    if (!rawXAxis || !rawSeries) return { xAxis: [], series: [] };
    const parsedDates = rawXAxis[0]?.data?.map((d) => dayjs(d));
    if (!parsedDates) return { xAxis: [], series: [] };

    let groups = {};
    parsedDates.forEach((date, idx) => {
      let key = "";
      if (format === "daily") key = date.format("YYYY-MM-DD");
      if (format === "weekly") key = `${date.year()}-W${date.isoWeek()}`;
      if (format === "monthly") key = date.format("YYYY-MM");
      if (!groups[key]) groups[key] = [];
      groups[key].push(idx);
    });

    const newXAxis = Object.keys(groups);
    const newSeries = rawSeries.map((serie) => {
      const groupedData = newXAxis.map((group) => {
        const indices = groups[group];
        const values = indices.map((i) => serie.data[i] || 0);
        return values.reduce((a, b) => a + b, 0) / values.length;
      });
      return { ...serie, data: groupedData };
    });

    return {
      xAxis: [{ scaleType: "band", data: newXAxis }],
      series: newSeries,
    };
  };

  const { xAxis: transformedXAxis, series: transformedSeries } = useMemo(
    () => transformDataByFormat(xAxis, series, timeFormat),
    [xAxis, series, timeFormat],
  );

  // Chart data processing
  const pieChartData = useMemo(
    () =>
      transformedSeries?.map((item, index) => ({
        id: index,
        value: item?.data?.reduce((a, b) => a + b, 0),
        label: item.label,
      })),
    [transformedSeries],
  );

  const scatterSeries = useMemo(
    () =>
      transformedSeries?.map((item) => ({
        label: item.label,
        color: item.color,
        data: item?.data?.map((value, index) => ({
          x: index,
          y: value,
        })),
      })),
    [transformedSeries],
  );

  // Generate PDF
  const generatePdf = useCallback(() => {
    GENERATEREPORT(
      ApiURL.generate_report_com,
      voyage,
      startDates,
      endDates,
      selectedReport,
    );
  }, [voyage, startDates, endDates, selectedReport]);

  // Fetch engine options
  const fetchEngineOptions = () => {
    const formData = new FormData();
    post(ApiURL.all_machinary_on_ship_st, formData).then((res) => {
      if (res?.status == true) {
        const options = Object.entries(res.data).flatMap(([key, items]) =>
          items.map((item) => ({
            value: item?.type,
            label: item.name,
          })),
        );
        if(options?.length > 0){
          fetchParameterOptions([options[0]?.value]);
        }
        setEngineOptions(options);
      }
    });
  };

  // Fetch parameter options
  const fetchParameterOptions = (initialTypes) => {
    const formData = new FormData();

    let mergedTypes = [];

    if (initialTypes) {
      // Called from fetchEngineOptions with initial engine types
      mergedTypes = [...initialTypes, ...selectedLogbook];
    } else if (selectedLogbook.includes("engine_logbook")) {
      mergedTypes = [...engineType, ...selectedLogbook];
    } else {
      mergedTypes = [...selectedLogbook];
    }

    const filteredTypes = mergedTypes.filter(
      (item) => item !== "engine_logbook",
    );

    // Only call API when there are actual types to send
    if (filteredTypes.length === 0) return;

    formData.append("types", JSON.stringify(filteredTypes));

    post(ApiURL.staff_api_machine_fields_graph_st, formData).then((res) => {
      if (res?.status == true) {
        setParameterOptions(res.data);
      }
    });
  };

  const GET_VOYAGE_DETAILS = () => {
    try {
      const FORM_DATA = new FormData();
      post(ApiURL.staff_dashboard_info_st, FORM_DATA).then((res) => {
        if (res?.status == true) {
          setVoyageDetails(res?.data);
        }
      });
    } catch (error) {
      console.error("Error fetching voyage details:", error);
    }
  };

  useEffect(() => {
    if (engineType?.length > 0) {
      fetchParameterOptions();
    }
  }, [JSON.stringify(engineType), JSON.stringify(selectedLogbook)]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user_data"));
    setShip(userData?.data?.ship_details);
    fetchDashboardDetails();
    fetchEngineOptions();
    GET_VOYAGE_DETAILS();
  }, []);

  const userName = user?.data?.name || "Crew Member";

  return (
    <div className="premium-dashboard-bg" style={{ padding: "24px" }}>
      {/* Welcome Banner */}
      <div className="dashboard-welcome-banner fade-in-section" style={{ marginBottom: "24px" }}>
        <div className="welcome-banner-content">
          <div className="welcome-banner-left">
            <h1>👋 Welcome back, {userName}</h1>
            <p>Here's your ship operations overview for today</p>
          </div>
          <div className="welcome-banner-right">
            {designation && <span className="designation-badge">{designation}</span>}
            <span className="date-badge">📅 {dayjs().format("DD MMM YYYY")}</span>
          </div>
        </div>
      </div>

      {/* Top Section */}
      <Row gutter={[24, 24]} className="fade-in-section">
        <Col xs={24} xl={14}>
          <Card
            className="hover-lift-card"
            style={{
              height: "100%",
              borderRadius: "20px",
              boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
              border: "1px solid rgba(255,255,255,0.4)",
              backdropFilter: "blur(20px)",
              background: "rgba(255,255,255,0.85)",
            }}
            bodyStyle={{ padding: "28px" }}
          >
            <VoyageStatus voyageDetails={voyageDetails} />
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <ShipDetailsCard ship={ship} />
        </Col>
      </Row>

      {/* Pending Tasks Section */}
      <div className="fade-in-section" style={{ marginTop: "24px" }}>
        {(designation === "Chief Engineer" ||
          designation === "Captain" ||
          designation === "Chief Officer") && (
          <PendingTaskTab getActiveTab={GET_TAB_DATA_LIST} />
        )}
      </div>

      {/* Chart Section */}
      <div className="fade-in-section" style={{ marginTop: "24px" }}>
        <div className="section-header">
          <LineChartOutlined className="section-icon" style={{ color: "#E11236" }} />
          <h3>Performance Analytics</h3>
        </div>
        <Card
          className="chart-card hover-lift-card"
          bodyStyle={{ padding: "28px" }}
        >
          <Box sx={{ padding: 1, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 2,
              }}
            >
              <DateEngineChartSelector
                selectedDates={reportDates}
                setSelectedDates={setReportDates}
                engineType={engineType}
                setEngineType={setEngineType}
                selectedChart={selectedChart}
                setSelectedChart={setSelectedChart}
                selectedParameters={selectedParameters}
                setSelectedParameters={setSelectedParameters}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                Graph_data={fetchGraphData}
                engineOptions={engineOptions}
                parameterMappings={parameterOptions}
                timeFormat={timeFormat}
                setTimeFormat={setTimeFormat}
                setSelectedLogbook={setSelectedLogbook}
                selectedLogbook={selectedLogbook}
              />
            </Box>

            {/* Chart */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                gap: "10px",
                marginTop: 2,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "450px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  align="center"
                  style={{ fontWeight: 700, color: "#0f172a", textTransform: "capitalize" }}
                >
                  {selectedChart} Chart ({timeFormat})
                </Typography>

                {selectedChart === "bar" && (
                  <BarChart
                    height={410}
                    margin={{ top: 80, bottom: 30, left: 40, right: 40 }}
                    series={transformedSeries}
                    xAxis={transformedXAxis}
                    slotProps={{
                      legend: { hidden: true },
                    }}
                  />
                )}
                {selectedChart === "line" && (
                  <LineChart
                    height={410}
                    margin={{ top: 80, bottom: 30, left: 40, right: 40 }}
                    series={transformedSeries}
                    xAxis={transformedXAxis}
                    slotProps={{
                      legend: { hidden: true },
                    }}
                  />
                )}
                {selectedChart === "pie" && (
                  <PieChart
                    height={410}
                    margin={{ top: 50, bottom: 30, left: -70 }}
                    series={[{ data: pieChartData }]}
                    slotProps={{
                      legend: { hidden: true },
                    }}
                  />
                )}
                {selectedChart === "scatter" && (
                  <ScatterChart
                    height={410}
                    margin={{ top: 80, bottom: 30, left: 40, right: 40 }}
                    series={scatterSeries}
                    slotProps={{
                      legend: { hidden: true },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Card>
      </div>

      {/* Report Section */}
      <div className="fade-in-section" style={{ marginTop: "24px" }}>
        <div className="section-header">
          <FileTextOutlined className="section-icon" style={{ color: "#E11236" }} />
          <h3>Generate Report</h3>
        </div>
        <Card
          className="report-card hover-lift-card"
          bodyStyle={{ padding: "28px" }}
        >
          <GenerateReportToolstaff
            selectedDates={reportDates}
            setSelectedDates={setReportDates}
            voyage={voyage}
            setVoyage={setVoyage}
            selectedReport={selectedReport}
            setselectedReport={setSelectedReport}
            setStartDates={setStartDates}
            setEndDates={setEndDates}
            filteredData={voyageList}
            generatePdf={generatePdf}
          />
        </Card>
      </div>
    </div>
  );
}

export default StaffDashboard;
